import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import MenuItem from '@mui/material/MenuItem';
import Iconify from './iconify';
import { useNavigate } from 'react-router-dom';
import Menu, { MenuProps } from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import useResponsive from '../hooks/useResponsive';
import { SplitButtonOptions } from '../@types/splitButtonOptions';
import { useI18n } from 'iomart-hooks';

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiBox-root': {
        fontSize: 14,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

type Props = {
  options: SplitButtonOptions;
  dense?: boolean;
};

export default function SplitButton({ options, dense }: Props) {
   const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
   const open = Boolean(anchorEl);
   const navigate = useNavigate();
   const isDesktop = useResponsive('up', 'md');
   const { t } = useI18n(false);

   const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
     setAnchorEl(event.currentTarget);
   };

   const handleMenuItemClick = (index) => {
     navigate(options[index].path);
     setAnchorEl(null);
   };

   const handleClose = () => {
     setAnchorEl(null);
   };

  return (
    <div>
      <ButtonGroup size={dense ? 'small' : 'medium'} variant="contained" aria-label="split button">
        {!isDesktop && (
          <Button onClick={() => handleMenuItemClick(0)}>
            <Iconify icon={options[0].icon || 'eva:plus-fill'} />
          </Button>
        )}
        {isDesktop && (
          <Button
            onClick={() => handleMenuItemClick(0)}
            startIcon={<Iconify icon={options[0].icon || 'eva:plus-fill'} />}
          >
            {t(options[0].title)}
          </Button>
        )}
        {options.length === 2 && (
          <Button onClick={() => handleMenuItemClick(1)}>
            <Iconify icon={options[1].icon || 'eva:plus-fill'} />
          </Button>
        )}
        {options.length > 2 && (
          <Button
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="select merge strategy"
            aria-haspopup="menu"
            onClick={handleClick}
          >
            <Iconify icon={'eva:arrow-down-fill'} />
          </Button>
        )}
      </ButtonGroup>
      {options.length > 2 && (
        <StyledMenu
          id="demo-customized-menu"
          MenuListProps={{
            'aria-labelledby': 'demo-customized-button',
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          {options
            .filter((_, index) => index !== 0)
            .map((option, index) => (
              <div key={index}>
                {option.divider && <Divider sx={{ my: 0.5 }} />}
                <MenuItem dense={dense} onClick={() => handleMenuItemClick(index)} disableRipple>
                  {option.icon && <Iconify icon={option.icon} />}
                  {t(option.title)}
                </MenuItem>
              </div>
            ))}
        </StyledMenu>
      )}
    </div>
  );
}
